import React, { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { InputNumber, Select, Icon } from 'antd';
import { cancellablePromise, useCancellablePromise } from './cancellablePromise';
import { COMPLEX_PAGE_OPTIONS } from './translationValues';
import { useDocumentContext } from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import { paperSizes } from '../../utils/complexPaperSizes';
import { LayoutContainer, PageLayoutInputLabel, BackgroundColorBox, PageBackgroundPicker } from './styles';
import ColorPicker from '../../artboard/ColorPicker';
import { measurementConversion } from '../../utils/javascript';
import { PAGE_PLEX, PAGE_MEDIA_TYPE } from '../../utils/complexDocumentConstants';
import PageNumberSettings from './CreativeBlockDesign/PageNumber/PageNumberSettings';
import { useShowVersionSettings } from './VersionSettings/useShowVersionSettings';
import OverLayText from './CreativeBlockDesign/OverLay/OverLayText';

const { Option } = Select;

const getMarginName = (marginIndex) => {
  switch (marginIndex) {
    case 0 :
      return 'marginTop';
    case 1 :
      return 'marginRight';
    case 2 :
      return 'marginBottom';
    case 3 :
      return 'marginLeft';
    default :
      return null;
  }
}

const NAMESPACES = ['COMPLEX_PAGE_OPTIONS'];

const ComplexPageLayoutSettings = ({}) => {
  const currentPageSelector = useCallback(store => store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE] ? store[CONTEXT_KEYS.CURRENT_PAGE] : '', []);
  const [currentPageUUID, updateArtboard] = useArtboardContext(currentPageSelector, `ComplexPageLayoutSettings`);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector);

  const visibilitySelector = useCallback(store => store[CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE], []);
  const [isVisible] = useArtboardContext(visibilitySelector);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexPageLayoutSettings`);

  const { showVersionSettings } = useShowVersionSettings(`ComplexPageLayoutSettings`);

  const topMargin = useRef(null);
  const rightMargin = useRef(null);
  const bottomMargin = useRef(null);
  const leftMargin = useRef(null);
  const changes = useRef({});
  const colorPickerContainerRef = useRef(null);
  const { t } = useTranslation(NAMESPACES);

  const { addCancellablePromise, removePendingPromise, clearPendingPromises } = useCancellablePromise();

  const customSelector = useCallback((store, previousState) => {
    return store[currentPageUUID];
  }, [currentPageUUID]);

  const [pageDetails, updatePageMethods] = useDocumentContext(customSelector, `ComplexPageLayoutSettings`);
  const [showPicker, toggleShowPicker] = useState(false);
  const [pageDetailBySize, setPageDetailsBySize] = useState({});
  const [isCustomSize, toggleCustomSize] = useState(false);
  const [sizeIn, setSizeIn] = useState('mm');

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign] = useArtboardContext(getCurrentDesign);

  useEffect(() => {
    if (pageDetails && pageDetails?.size === 'custom') {
      toggleCustomSize(true);
    } else {
      toggleCustomSize(false);
    }
  }, [pageDetails]);

  useEffect(() => {
    let pageSize = {
      height: 1123,
      heightInMM : 297,
      margin: [0, 0, 0, 0],    
      width: 794,
      widthInMM: 210
    } 
    if (pageDetails) {
      if(sizeIn === 'mm') {
        pageSize = {
          height: pageDetails.heightInMM,
          width: pageDetails.widthInMM,
          margin: [
            measurementConversion(pageDetails.margin[0], 'mm'),
            measurementConversion(pageDetails.margin[1], 'mm'),
            measurementConversion(pageDetails.margin[2], 'mm'),
            measurementConversion(pageDetails.margin[3], 'mm'),
          ],
        }
      } else {
        pageSize = {
          height: pageDetails.height,
          width: pageDetails.width,
          margin: pageDetails.margin,
        }        
      }
      setPageDetailsBySize(pageSize);
    } else {
      setPageDetailsBySize(pageSize);
    }
  }, [pageDetails, sizeIn]);

  useLayoutEffect(() => {
    if (!showPicker || !colorPickerContainerRef.current) return;
    const rect = colorPickerContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    if (rect.bottom > viewportHeight) {
      colorPickerContainerRef.current.style.transform = 'translate(0px, -390px)';
    }
  }, [showPicker]);

  const onPageSizeChange = useCallback((newPageSize) => {
    toggleCustomSize(newPageSize === 'custom');
    if(newPageSize === 'custom') {
      updatePageMethods.setFlowSettings('setPageSize', { uuid: currentPageUUID, size: newPageSize, orientation: 'portrait' });
    } else {
      // TODO : Decide whether or not to show confirmation, what happens to the blocks on change of page size or orientation
      updatePageMethods.setFlowSettings('setPageSize', { uuid: currentPageUUID, size: newPageSize, orientation: pageDetails?.orientation });
    }
  }, [currentPageUUID, pageDetails]);

  const onPageOrientationChange = useCallback((newPageOrientation) => {
    updatePageMethods.setFlowSettings('setPageOrientation', { uuid: currentPageUUID, size: pageDetails?.size, orientation: newPageOrientation });
  }, [currentPageUUID, pageDetails]);

  const onColorPick = useCallback(color => {
    if (color && color.hex) {
      updatePageMethods.setFlowSettings('setPageBackground', { backgroundColor: color.hex, uuid: currentPageUUID });
    }
  }, [currentPageUUID]);

  const onPlexChange = useCallback(value => {
    updatePageMethods.setFlowSettings('setPagePlex', { uuid: currentPageUUID, plex: value });
  }, [currentPageUUID]);

  const onMediaTypeChange = useCallback(value => {
    updatePageMethods.setFlowSettings('setPageMediaType', { uuid: currentPageUUID, mediaType: value });
  }, [currentPageUUID]);

  const updateHeightWidth = useCallback(async (value, size) => {
    clearPendingPromises();
    const cancellablePromise1 = cancellablePromise(new Promise(resolve => setTimeout(resolve, 1000)));
    changes.current = Object.assign({}, changes.current, {
      uuid: currentPageUUID,
      [size]: value
    });
    addCancellablePromise(cancellablePromise1);

    try {
      await cancellablePromise1.promise;
      let convertedValues =  {};
      let resizeSuccess;
      if(size === 'height') {
        if(sizeIn === 'px') {
          convertedValues = {
            heightInPX: value,
            heightInMM: measurementConversion(value, 'mm'),
          }
        } else {
          convertedValues = {
            heightInPX: measurementConversion(value, 'px'),
            heightInMM: value,
          }
        }
        resizeSuccess =updatePageMethods.setFlowSettings('setPageHeight', { uuid: currentPageUUID, size: 'custom', ...convertedValues, orientation: 'portrait' });
      } else {
        if(sizeIn === 'px') {
          convertedValues = {
            widthInPX: value,
            widthInMM: measurementConversion(value, 'mm'),
          }
        } else {
          convertedValues = {
            widthInPX: measurementConversion(value, 'px'),
            widthInMM: value,
          }
        }
        resizeSuccess = updatePageMethods.setFlowSettings('setPageWidth', { uuid: currentPageUUID, size: 'custom', ...convertedValues, orientation: 'portrait'});
      }
    } catch (error) {
      removePendingPromise(cancellablePromise1);
    }
  }, [currentPageUUID, pageDetails, sizeIn]);

  const updateMargin = useCallback(async (value, marginIndex) => {
    clearPendingPromises();
    let convertedValue;
    if(sizeIn === 'px') {
      convertedValue = value
    } else {
      convertedValue = measurementConversion(value, 'px')
    }
    const cancellablePromise1 = cancellablePromise(new Promise(resolve => setTimeout(resolve, 1000)));
    let marginName = getMarginName(marginIndex);
    changes.current = Object.assign({}, changes.current, {
      uuid: currentPageUUID,
      [marginName]: convertedValue
    });
    addCancellablePromise(cancellablePromise1);

    try {
      await cancellablePromise1.promise;
      const resizeSuccess = updatePageMethods.setFlowSettings('setPageMargin', changes.current);
    } catch (error) {
      removePendingPromise(cancellablePromise1);
    }
  }, [currentPageUUID, sizeIn]);
  
  useEffect(() => {
    changes.current = {};
  }, [currentPageUUID])

  if (!isVisible || pageDetails === undefined || pageDetails === null) return <div />;

  return (
  <>
  {ReactDOM.createPortal(
    <LayoutContainer isPageSettings={1} onKeyDown={e => e.stopPropagation()} disabled={isInteractiveDesign || showVersionSettings}>
      <div className='title'>{t(COMPLEX_PAGE_OPTIONS.PAGE_SETTING_HEADER.key, COMPLEX_PAGE_OPTIONS.PAGE_SETTING_HEADER.value)}
        <div className="size-container">
          <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.CHANGE_SIZE_LABEL.key, COMPLEX_PAGE_OPTIONS.CHANGE_SIZE_LABEL.value)} </PageLayoutInputLabel>
          <Select value={sizeIn} style={{ height: '38px' }} onChange={val => setSizeIn(val)}>
            <Option value="px"> px </Option>
            <Option value="mm"> mm </Option>
          </Select>
        </div>
      </div>
      <div className='properties-container'>
        <div className='grid' style={{ alignItems: 'center', marginBottom: '20px' }}>
          <PageLayoutInputLabel>{t(COMPLEX_PAGE_OPTIONS.PAGE_SIZE_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_SIZE_LABEL.value)} </PageLayoutInputLabel>
          <Select value={pageDetails?.size} style={{ flex: 1, marginLeft: '10px' }} onChange={onPageSizeChange} disabled={!editable}>
            {paperSizes.map(paperSize => (
              <Option value={paperSize.lowercaseName}> {paperSize.name} </Option>
            ))}
          </Select>
        </div>
        <div className='grid' style={{ alignItems: 'center', marginBottom: '20px' }}>
          <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_ORIENTATION_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_ORIENTATION_LABEL.value)} </PageLayoutInputLabel>
          <Select disabled={!editable && isCustomSize} value={pageDetails?.orientation} style={{ flex: 1, marginLeft: '10px' }} onChange={onPageOrientationChange}>
            <Option value={'portrait'}> {t(COMPLEX_PAGE_OPTIONS.PAGE_ORIENTATION_PORTRAIT_OPTION.key, COMPLEX_PAGE_OPTIONS.PAGE_ORIENTATION_PORTRAIT_OPTION.value)} </Option>
            <Option value={'landscape'}> {t(COMPLEX_PAGE_OPTIONS.PAGE_ORIENTATION_LANDSCAPE_OPTION.key, COMPLEX_PAGE_OPTIONS.PAGE_ORIENTATION_LANDSCAPE_OPTION.value)} </Option>
          </Select>
        </div>
        <div className="grid">
          <div className="input-container">
            <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_HEIGHT_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_HEIGHT_LABEL.value)} </PageLayoutInputLabel>
            <InputNumber
              disabled={!editable || !isCustomSize}
              type="number"
              size="large"
              value={pageDetailBySize?.height}
              onChange={e => updateHeightWidth(parseInt(e), 'height')}
            />
          </div>
          <div className="input-container">
            <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_WIDTH_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_WIDTH_LABEL.value)} </PageLayoutInputLabel>
            <InputNumber
              disabled={!editable || !isCustomSize}
              ref={bottomMargin}
              type="number"
              size="large"
              value={pageDetailBySize?.width}
              onChange={e => updateHeightWidth(parseInt(e), 'width')}
            />
          </div>
        </div>
        <div className='grid' style={{ alignItems: 'center', marginBottom: '20px' }}>
          <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_BACKGROUND_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_BACKGROUND_LABEL.value)} </PageLayoutInputLabel>
          <BackgroundColorBox
            style={{ backgroundColor: pageDetails?.backgroundColor, marginLeft: '10px' }}
            onClick={() => toggleShowPicker(true)}
            disabled={!editable || isInteractiveDesign}
          >
            {showPicker && (
              <PageBackgroundPicker ref={colorPickerContainerRef} style={{height: '366px'}}>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => toggleShowPicker(false)}
                  color={pageDetails?.backgroundColor}
                  qlEditor={true}
                />
              </PageBackgroundPicker>
            )}
          </BackgroundColorBox>
        </div>
        <div className='grid'>
          <div className='input-container'>
            <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_TOP_MARGIN_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_TOP_MARGIN_LABEL.value)} </PageLayoutInputLabel>
            <InputNumber
              ref={topMargin}
              type="number"
              size="large"
              value={pageDetailBySize?.margin[0]}
              onChange={value => updateMargin(value, 0)}
              min={0}
              max={10000}
              disabled={!editable}
            />
          </div>
          <div className='input-container'>
            <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_BOTTOM_MARGIN_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_BOTTOM_MARGIN_LABEL.value)} </PageLayoutInputLabel>
            <InputNumber
              ref={bottomMargin}
              type="number"
              size="large"
              min={0}
              max={10000}
              value={pageDetailBySize?.margin[2]}
              onChange={value => updateMargin(value, 2)}
              disabled={!editable}
            />
          </div>
        </div>
        <div className='grid'>
          <div className='input-container'>
            <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_LEFT_MARGIN_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_LEFT_MARGIN_LABEL.value)} </PageLayoutInputLabel>
            <InputNumber
              ref={leftMargin}
              type="number"
              size="large"
              value={pageDetailBySize?.margin[3]}
              min={0}
              max={10000}
              onChange={value => updateMargin(value, 3)}
              disabled={!editable}
            />
          </div>
          <div className='input-container'>
            <PageLayoutInputLabel> {t(COMPLEX_PAGE_OPTIONS.PAGE_RIGHT_MARGIN_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_RIGHT_MARGIN_LABEL.value)} </PageLayoutInputLabel>
            <InputNumber
              ref={rightMargin}
              type="number"
              size="large"
              min={0}
              max={10000}
              value={pageDetailBySize?.margin[1]}
              onChange={value => updateMargin(value, 1)}
              disabled={!editable}
            />
          </div>
        </div>
      </div>
      {/* Print Settings */}
      <div>
        <div className='title'>
          {t(COMPLEX_PAGE_OPTIONS.PRINT_SETTINGS_TITLE.key, COMPLEX_PAGE_OPTIONS.PRINT_SETTINGS_TITLE.value)}
        </div>
        <div className='properties-container'>
          <div className='grid' style={{ alignItems: 'center', marginBottom: '20px' }}>
            <PageLayoutInputLabel>
              {t(COMPLEX_PAGE_OPTIONS.PRINT_PLEX_LABEL.key, COMPLEX_PAGE_OPTIONS.PRINT_PLEX_LABEL.value)}
            </PageLayoutInputLabel>
            <Select
              value={pageDetails?.plex || PAGE_PLEX.SIMPLEX}
              style={{ flex: 1, marginLeft: '10px' }}
              onChange={onPlexChange}
              disabled={!editable}
            >
              <Option value={PAGE_PLEX.SIMPLEX}>
                {t(
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_SIMPLEX.key,
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_SIMPLEX.value,
                )}
              </Option>
              <Option value={PAGE_PLEX.DUPLEX}>
                {t(
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_DUPLEX.key,
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_DUPLEX.value,
                )}
              </Option>
              <Option value={PAGE_PLEX.DUPLEX_FRONT}>
                {t(
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_DUPLEX_FRONT.key,
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_DUPLEX_FRONT.value,
                )}
              </Option>
              <Option value={PAGE_PLEX.DUPLEX_BACK}>
                {t(
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_DUPLEX_BACK.key,
                  COMPLEX_PAGE_OPTIONS.PRINT_PLEX_OPTION_DUPLEX_BACK.value,
                )}
              </Option>
            </Select>
          </div>
          <div className='grid' style={{ alignItems: 'center' }}>
            <PageLayoutInputLabel>
              {t(COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_LABEL.key, COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_LABEL.value)}
            </PageLayoutInputLabel>
            <Select
              value={pageDetails?.mediaType || PAGE_MEDIA_TYPE.PLAIN}
              style={{ flex: 1, marginLeft: '10px' }}
              onChange={onMediaTypeChange}
              disabled={!editable}
            >
              <Option value={PAGE_MEDIA_TYPE.PLAIN}>
                {t(
                  COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_OPTION_PLAIN.key,
                  COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_OPTION_PLAIN.value,
                )}
              </Option>
              <Option value={PAGE_MEDIA_TYPE.LETTERHEAD}>
                {t(
                  COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_OPTION_LETTERHEAD.key,
                  COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_OPTION_LETTERHEAD.value,
                )}
              </Option>
              <Option value={PAGE_MEDIA_TYPE.PREPRINTED}>
                {t(
                  COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_OPTION_PREPRINTED.key,
                  COMPLEX_PAGE_OPTIONS.PRINT_MEDIA_TYPE_OPTION_PREPRINTED.value,
                )}
              </Option>
            </Select>
          </div>
        </div>
      </div>
      {/* OverLay Text */}
      <div>
        <div className='title'>{t(COMPLEX_PAGE_OPTIONS.TEXT_OVERLAY_LABEL.key, COMPLEX_PAGE_OPTIONS.TEXT_OVERLAY_LABEL.value)}</div>
        <OverLayText uuid={currentPageUUID}/>
      </div>
      {CurrentDesign === 'document' &&
        <div>
          <div className='title'>{t(COMPLEX_PAGE_OPTIONS.PAGE_NUMBER_LABEL.key, COMPLEX_PAGE_OPTIONS.PAGE_NUMBER_LABEL.value)}</div>
          <PageNumberSettings flowUUID={currentPageUUID} sizeIn={sizeIn} />
        </div>
      }    
    </LayoutContainer>
  , document.getElementById('sidebar-absolute-container'))}
  {ReactDOM.createPortal(
    <div
      className="layoutbtn-container"
      onClick={() => {
        updateArtboard({ [CONTEXT_KEYS.CURRENT_PAGE]: '', [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false });
      }}
    >
      <Icon type={'close'} />
    </div>
    , document.getElementById('sidebar-absolute-close')
  )}
  </>);
}

export default ComplexPageLayoutSettings;